import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { cn } from '@/libs/utils';

/**
 * Left column of the profile: tiles in two columns, grouped. When a group has
 * an odd number of tiles the last one spans the full width, so there are no
 * holes in the grid. The column never scrolls: groups share its height by
 * their number of rows and tiles shrink on low screens. A tile's lines wrap
 * into a hidden second column when they do not fit, so a line that has no
 * room disappears whole instead of being cut through (alerts come first).
 */
export default function ProfileTiles({
  groups,
  selected,
  onSelect,
  renderTile,
  label,
  maxRowHeight,
}) {
  // One grid for the whole column: every tile row gets the same share of the
  // height, group titles take only what they need. `maxRowHeight` caps the
  // share when only a few tiles are left, so they do not stretch to fill it.
  const row = maxRowHeight
    ? `minmax(0, ${maxRowHeight}px)`
    : 'minmax(0, 1fr)';
  const template = groups
    .map((g) => `auto repeat(${Math.ceil(g.tiles.length / 2)}, ${row})`)
    .join(' ');

  return (
    <nav
      aria-label={label}
      data-tour="profile-tiles"
      className="grid h-full min-h-0 grid-cols-2 gap-x-2 gap-y-1.5 overflow-hidden rounded-[14px] bg-white px-3 pb-3 pt-1.5 [@media(max-height:820px)]:gap-y-1"
      style={{
        gridTemplateRows: template,
        ...(maxRowHeight && { alignContent: 'start' }),
      }}
    >
      {groups.map((group) => {
        return (
          <div key={group.id} className="contents">
            <h2 className="col-span-2 mb-0 mt-1 text-[11px] font-bold uppercase tracking-[0.07em] text-zinc-400">
              {group.title}
            </h2>

            {group.tiles.map((id, idx) => {
              const tile = renderTile(id);
              const wide =
                group.tiles.length % 2 === 1 && idx === group.tiles.length - 1;
              const isOn = id === selected;

              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => onSelect(id)}
                  className={cn(
                    'flex min-h-0 min-w-0 cursor-pointer flex-col flex-wrap content-start gap-x-6 overflow-hidden rounded-xl border-2 border-zinc-300 bg-white px-3 py-1.5 text-left shadow-[0_1px_3px_rgba(16,24,40,0.10)] transition-all hover:-translate-y-px hover:border-accent hover:shadow-[0_4px_10px_rgba(16,24,40,0.12)] active:translate-y-0 [@media(max-height:820px)]:py-1',
                    wide && 'col-span-2',
                    isOn &&
                      'border-accent bg-[rgba(var(--accentColor),0.08)] shadow-[0_0_0_3px_rgba(var(--accentColor),0.15)]'
                  )}
                >
                  <span className="flex w-full shrink-0 items-center justify-between gap-1.5 text-[12px] font-bold leading-4 text-accent">
                    <span className="flex min-w-0 items-center gap-1">
                      {/* A long name takes a second line rather than being
                          cut — except on low screens, where that line would
                          push the value out of the tile: there it stays on
                          one line, whole on hover. */}
                      <span
                        className="line-clamp-2 [@media(max-height:1000px)]:line-clamp-1"
                        title={tile.label}
                      >
                        {tile.label}
                      </span>
                      {tile.info && (
                        <Tooltip title={tile.info}>
                          <QuestionCircleOutlined className="shrink-0 text-zinc-400" />
                        </Tooltip>
                      )}
                    </span>
                    {tile.aside && (
                      <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] font-semibold text-zinc-500">
                        {tile.aside}
                      </span>
                    )}
                    {tile.dirty && (
                      <Tooltip title={tile.dirtyLabel}>
                        <span
                          aria-label={tile.dirtyLabel}
                          className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                        />
                      </Tooltip>
                    )}
                  </span>
                  {/* Second aside line, right under the first and above the
                      value, so it always shows like the title row. On a
                      narrow tile the label is cut, never the date. */}
                  {tile.aside2 && (
                    <span
                      title={[tile.aside2, tile.aside2Date].filter(Boolean).join(' ')}
                      className="flex w-full shrink-0 justify-end gap-1 text-[11px] font-semibold leading-4 text-zinc-500"
                    >
                      <span className="min-w-0 truncate">{tile.aside2}</span>
                      {tile.aside2Date && (
                        <span className="shrink-0 whitespace-nowrap">
                          {tile.aside2Date}
                        </span>
                      )}
                    </span>
                  )}
                  <span className="w-full shrink-0 truncate text-[15px] font-extrabold leading-[18px] text-zinc-900 [@media(max-height:820px)]:text-[13px]">
                    {tile.value}
                  </span>
                  {tile.alert && (
                    <span
                      data-tour="profile-tile-alert"
                      className="w-full shrink-0 truncate text-[11px] font-bold text-red-700">
                      {tile.alert}
                    </span>
                  )}
                  {tile.warn && (
                    <span
                      data-tour="profile-tile-alert"
                      className="w-full shrink-0 truncate text-[11px] font-bold text-amber-700">
                      {tile.warn}
                    </span>
                  )}
                  {tile.hint && (
                    <span className="w-full shrink-0 truncate text-[11px] text-zinc-500">
                      {tile.hint}
                    </span>
                  )}
                  {tile.hint2 && (
                    <span className="w-full shrink-0 truncate text-[11px] text-zinc-500">
                      {tile.hint2}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
